/**
 * Excel / CSV financial-model extractor (FA pack, FA2).
 *
 * Three-tier extraction with descending confidence:
 *
 *   Tier 1 — NAMED RANGES  (high confidence)
 *     Looks up well-known names like `LCOE_NOMINAL`, `CAPEX_TOTAL`
 *     in `workbook.Workbook?.Names`. Direct deterministic mapping;
 *     cellReference is recorded.
 *
 *   Tier 2 — LABEL MATCHING (medium confidence)
 *     Scans every cell on every sheet for labels matching a
 *     per-field synonym table. The value is the adjacent cell
 *     (right neighbor first, then below). Multiple candidates →
 *     prefer cells on sheets named Inputs / Summary / Outputs and
 *     record a warning.
 *
 *   Tier 3 — LLM-ASSISTED (low confidence)
 *     Pluggable callback `llmExtractor` — receives a markdown render
 *     of each sheet and returns whatever values it can locate.
 *     Defaults to a no-op so this module has no hard dependency on
 *     the Gemini client; callers wire it in.
 *
 * Pure-deterministic for tiers 1+2 — every value can be replayed
 * given the same input file. Tier 3 is best-effort and every value
 * gets `confidence: 'low'`; the post-upload review modal (FA5) shows
 * tier-3 values in their own bucket for user confirmation.
 *
 * The extractor never throws on a malformed cell — it logs a warning
 * and falls through. Empty workbooks produce a zero-result envelope.
 */
import * as XLSX from 'xlsx';
import type {
  CurrencyCode,
  FieldProvenance,
  FieldProvenanceConfidence,
  FinancialAssumptionsCore,
  FinancialField,
} from '../../types/financialAssumptions';

export interface ExtractionResult {
  /** Sparse — only fields the extractor managed to fill. */
  assumptions: Partial<FinancialAssumptionsCore>;
  /** Per-field provenance; same key set as `assumptions`. */
  fieldProvenance: Partial<Record<FinancialField, FieldProvenance>>;
  /**
   * Non-fatal observations — e.g. "Found two cells labelled LCOE —
   * picked Inputs!B14". The review modal surfaces these to the user.
   */
  warnings: string[];
  /**
   * Per-field CAUTIONS the user must resolve — an implausible $/kW, an
   * ambiguous debt:equity reading. Distinct from `warnings`: a flag
   * names the fields it applies to and can force a per-row decision.
   * The extractor never silently corrects the values it flags.
   */
  reviewFlags: ExtractionReviewFlag[];
  /**
   * The workbook's currency when one could be read from a unit
   * position, else null. Nothing is converted — the engine computes in
   * USD and the review modal makes the user declare the rate.
   */
  detectedCurrency: DetectedCurrency | null;
  /**
   * Confidence-tier counts for the review-modal summary chip.
   * `missing` = the union of canonical fields the extractor failed
   * to fill.
   */
  summary: {
    highConfidence: number;
    mediumConfidence: number;
    lowConfidence: number;
    missing: number;
  };
}

/**
 * Pluggable tier-3 callback. Receives the still-unfilled fields and
 * the workbook rendered as a markdown table per sheet. Returns its
 * best guesses with cell references when available.
 *
 * `null` ⇒ the LLM tier was not run (no callback wired, or the
 * caller opted out). The extractor handles `null` identically to an
 * empty map.
 */
export type LlmExtractor = (
  missingFields: FinancialField[],
  sheetsMarkdown: Array<{ sheet: string; markdown: string }>,
) => Promise<Partial<Record<FinancialField, { value: number | string; sheet?: string; cell?: string }>>>;

export interface ExtractFinancialModelOptions {
  /** Tier-3 callback. Defaults to a no-op when not provided. */
  llmExtractor?: LlmExtractor;
  /**
   * For testing / determinism — override the ISO timestamp stamped on
   * each FieldProvenance.extractedAt. Defaults to `new Date().toISOString()`.
   */
  now?: string;
}

/**
 * Tier-1 lookup — well-known named ranges → canonical field. The
 * comparison is case-insensitive and ignores spaces / underscores so
 * `LCOE_NOMINAL`, `lcoe.nominal`, `LCOENominal` all hit the same row.
 */
const NAMED_RANGE_MAP: Record<string, FinancialField> = {
  LCOE_NOMINAL: 'lcoeNominalPerMwhUsd',
  LCOE_REAL: 'lcoeRealPerMwhUsd',
  CAPEX_TOTAL: 'capexTotalUsd',
  CAPEX_PER_KW: 'capexPerKwUsd',
  CAPEX_PER_MW: 'capexPerKwUsd',          // user-facing $/MW gets divided at parse
  PROJECT_IRR: 'projectIrrPct',
  EQUITY_IRR: 'equityIrrPct',
  NPV: 'npvUsd',
  PAYBACK_YEARS: 'paybackYears',
  PAYBACK: 'paybackYears',
  CAPACITY_MW: 'capacityMW',
  CAPACITY: 'capacityMW',
  CAPACITY_FACTOR: 'capacityFactorPct',
  PROJECT_LIFE: 'projectLifeYears',
  PROJECT_LIFE_YEARS: 'projectLifeYears',
  IN_SERVICE_DATE: 'inServiceDate',
  COD: 'inServiceDate',
  CONSTRUCTION_MONTHS: 'constructionMonths',
  DECOMMISSIONING: 'decommissioningUsd',
  WORKING_CAPITAL: 'workingCapitalUsd',
  CONTINGENCY: 'contingencyPct',
  FIXED_OM: 'fixedOmPerKwYearUsd',
  FIXED_OM_PER_KW: 'fixedOmPerKwYearUsd',
  VARIABLE_OM: 'variableOmPerMwhUsd',
  VARIABLE_OM_PER_MWH: 'variableOmPerMwhUsd',
  FUEL_COST: 'fuelCostPerMwhUsd',
  FUEL_PRICE: 'fuelCostPerMwhUsd',
  INSURANCE_PROPERTY_TAX: 'insuranceAndPropertyTaxPct',
  DEBT_EQUITY_RATIO: 'debtToEquityRatio',
  DEBT_RATIO: 'debtToEquityRatio',
  DEBT_RATE: 'debtRatePct',
  EQUITY_HURDLE: 'equityHurdlePct',
  EQUITY_RATE: 'equityHurdlePct',
  WACC: 'waccPct',
  TAX_RATE: 'taxRatePct',
  ITC: 'itcPct',
  PTC: 'ptcPerMwhUsd',
  PPA_PRICE: 'ppaPricePerMwhUsd',
  PPA_ESCALATOR: 'ppaEscalatorPctPerYear',
  PPA_TERM: 'ppaTermYears',
};

/**
 * Tier-2 synonym table. Each canonical field has a list of
 * human-readable labels we'll match (case-insensitive, ignoring
 * trailing colons / units / parentheticals). Matching uses substring
 * after normalisation — a cell labelled `"LCOE (nominal, $/MWh)"`
 * matches `LCOE` and `LCOE Nominal` cleanly.
 */
const LABEL_SYNONYMS: Partial<Record<FinancialField, string[]>> = {
  capacityMW: ['Capacity', 'Capacity (MW)', 'Nameplate', 'Plant Capacity', 'Rated Capacity'],
  capacityFactorPct: ['Capacity Factor', 'CF', 'Cap. Factor', 'Net Capacity Factor'],
  projectLifeYears: ['Project Life', 'Plant Life', 'Useful Life', 'Operating Life'],
  inServiceDate: ['In-Service Date', 'COD', 'Commercial Operation Date', 'In Service Date'],
  capexTotalUsd: ['Total CAPEX', 'Total Project Cost', 'Total Capital', 'CAPEX Total', 'Installed Cost', 'Overnight Cost'],
  capexPerKwUsd: ['CAPEX per kW', '$/kW', '$ per kW', 'Unit CAPEX'],
  constructionMonths: ['Construction Period', 'Construction Months', 'Build Time'],
  decommissioningUsd: ['Decommissioning', 'Decommissioning Cost', 'Decommissioning Reserve'],
  workingCapitalUsd: ['Working Capital'],
  contingencyPct: ['Contingency'],
  fixedOmPerKwYearUsd: ['Fixed O&M', 'Fixed OPEX', 'Fixed O&M per kW', '$/kW-yr', 'Fixed Operations'],
  variableOmPerMwhUsd: ['Variable O&M', 'Variable OPEX', 'Variable O&M per MWh'],
  fuelCostPerMwhUsd: ['Fuel Cost', 'Fuel Price', 'Fuel'],
  insuranceAndPropertyTaxPct: ['Insurance', 'Property Tax', 'Insurance & Property Tax'],
  debtToEquityRatio: ['Debt:Equity', 'D:E Ratio', 'Debt to Equity', 'Leverage Ratio', 'Debt Share'],
  debtRatePct: ['Debt Rate', 'Cost of Debt', 'Interest Rate'],
  equityHurdlePct: ['Equity Hurdle', 'Cost of Equity', 'Equity IRR Target', 'Hurdle Rate'],
  waccPct: ['WACC', 'Weighted Average Cost of Capital', 'Discount Rate'],
  taxRatePct: ['Tax Rate', 'Corporate Tax', 'Income Tax Rate'],
  itcPct: ['ITC', 'Investment Tax Credit'],
  ptcPerMwhUsd: ['PTC', 'Production Tax Credit'],
  ppaPricePerMwhUsd: ['PPA Price', 'PPA $/MWh', 'Offtake Price', 'Contract Price'],
  ppaEscalatorPctPerYear: ['PPA Escalator', 'Annual Escalation', 'Price Escalator'],
  ppaTermYears: ['PPA Term', 'Contract Term', 'Offtake Term'],
  lcoeNominalPerMwhUsd: ['LCOE', 'Levelized Cost of Energy', 'Levelized cost', 'LCOE (nominal)', 'LCOE Nominal'],
  lcoeRealPerMwhUsd: ['LCOE Real', 'LCOE (real)', 'LCOE Real Terms'],
  npvUsd: ['NPV', 'Net Present Value'],
  projectIrrPct: ['Project IRR', 'IRR (project)', 'Internal Rate of Return', 'Unlevered IRR', 'IRR'],
  equityIrrPct: ['Equity IRR', 'Levered IRR'],
  paybackYears: ['Payback', 'Payback Period', 'Simple Payback'],
};

/**
 * Which canonical fields are percentages in the form (and therefore
 * should be multiplied by 100 when an extracted cell holds a
 * 0.0-1.0 fraction). Capacity factor at 0.92 in Excel ⇒ 92% in the
 * form.
 */
const PERCENT_FIELDS = new Set<FinancialField>([
  'capacityFactorPct',
  'contingencyPct',
  'insuranceAndPropertyTaxPct',
  'debtRatePct',
  'equityHurdlePct',
  'waccPct',
  'taxRatePct',
  'itcPct',
  'ppaEscalatorPctPerYear',
  'lcoeNominalPerMwhUsd',  // NOT a percent — listed elsewhere; kept out
].filter(f => f !== 'lcoeNominalPerMwhUsd') as FinancialField[]);

/**
 * `debtToEquityRatio` is stored as a 0-1 fraction (e.g. 0.7 ⇒ 70:30).
 * If Excel carries it as a ratio like 70:30 or 2.33:1, the parser
 * leaves it alone — there's no robust way to round-trip a colon ratio
 * from a cell. Users review medium-confidence hits.
 */

const PROJECT_IRR_FIELDS = new Set<FinancialField>(['projectIrrPct', 'equityIrrPct']);
const PREFERRED_SHEETS = ['inputs', 'summary', 'outputs', 'output', 'results', 'dashboard'];

// ─────────────────────────────────────────────────────────────────────
// Unit scale (F3) — `"Total CAPEX ($M) | 500"` is $500,000,000, not $500
// ─────────────────────────────────────────────────────────────────────

/**
 * Multipliers for the scale qualifier a financial model carries in its
 * label, its units column or its header row ("$k", "($M)", "USD
 * millions", "$'000"). Named so no bare `1e6` appears in the conversion
 * path and so the review UI can name the scale it applied.
 */
export const SCALE_MULTIPLIER_THOUSAND = 1_000;
export const SCALE_MULTIPLIER_MILLION = 1_000_000;
export const SCALE_MULTIPLIER_BILLION = 1_000_000_000;

export interface UnitScale {
  /** Factor applied to the cell value to reach base units (USD). */
  multiplier: number;
  /** Short label for provenance + review UI: `$k` / `$M` / `$bn`. */
  label: string;
}

const SCALE_THOUSAND: UnitScale = { multiplier: SCALE_MULTIPLIER_THOUSAND, label: '$k' };
const SCALE_MILLION: UnitScale = { multiplier: SCALE_MULTIPLIER_MILLION, label: '$M' };
const SCALE_BILLION: UnitScale = { multiplier: SCALE_MULTIPLIER_BILLION, label: '$bn' };

/**
 * Fields whose value is a USD MAGNITUDE (a total, not a rate), and are
 * therefore the only fields a `$k / $M / $bn` qualifier may rescale.
 *
 * Deliberately excludes per-unit money (`capexPerKwUsd`,
 * `lcoeNominalPerMwhUsd`, `ppaPricePerMwhUsd`, `fixedOmPerKwYearUsd`, …):
 * a `($/kW)` or `($/MWh)` header is a UNIT, not a scale, and rescaling a
 * rate by 10^6 is exactly the class of error this guard exists to stop.
 *
 * NOTE: `landCost.totalUsd` is a USD magnitude too, but this extractor
 * does not extract it (no `LABEL_SYNONYMS` entry, not in
 * `canonicalFields()`), so there is nothing to scale. If land-cost
 * extraction is added, add it here.
 */
export const USD_MAGNITUDE_FIELDS = new Set<FinancialField>([
  'capexTotalUsd',
  'npvUsd',
  'decommissioningUsd',
  'workingCapitalUsd',
]);

/** Every field carrying money in ANY form — magnitudes plus rates. */
export const MONETARY_FIELDS = new Set<FinancialField>([
  ...USD_MAGNITUDE_FIELDS,
  'capexPerKwUsd',
  'fixedOmPerKwYearUsd',
  'variableOmPerMwhUsd',
  'fuelCostPerMwhUsd',
  'ptcPerMwhUsd',
  'ppaPricePerMwhUsd',
  'lcoeNominalPerMwhUsd',
  'lcoeRealPerMwhUsd',
]);

/** How many rows from the top of a sheet count as its header/units band. */
const SCALE_HEADER_SCAN_ROWS = 5;
/** How many unit-only cells may sit between a label and its value. */
const MAX_UNITS_CELLS_BETWEEN = 2;
/** Separator used when a provenance cellReference carries a re-reading note. */
const PROVENANCE_NOTE_SEPARATOR = ' · ';

//  `($M)`, `US$m`, `EURm`, `$ bn`. Requires the letter to follow the
//  currency token directly, so `($/kW)` and `($ per kW)` never match.
const SCALE_SYMBOL_RE = /(?:us\$|\$|usd|eur|€|gbp|£|chf|zar|bwp)\s*(mm|bn|k|m|b)\b/;
//  `m$`, `bn USD`.
const SCALE_SYMBOL_SUFFIX_RE = /\b(mm|bn|k|m|b)\s*(?:us\$|\$|usd)\b/;
//  `USD millions`, `in thousands`, `(billions)`.
const SCALE_WORD_RE = /\b(thousand|million|billion)s?\b/;
//  `'000` / `000s` — the accountant's thousands marker. The apostrophe /
//  trailing `s` is required so `$1,000` is never read as a scale.
const SCALE_THOUSAND_TICK_RE = /(?:'0{3}|\b0{3}s)\b/;

function scaleFromToken(token: string): UnitScale | null {
  switch (token) {
    case 'k': return SCALE_THOUSAND;
    case 'm': case 'mm': return SCALE_MILLION;
    case 'b': case 'bn': return SCALE_BILLION;
    case 'thousand': return SCALE_THOUSAND;
    case 'million': return SCALE_MILLION;
    case 'billion': return SCALE_BILLION;
    default: return null;
  }
}

/**
 * Read a scale qualifier out of arbitrary label / header text.
 *
 * Returns null when the text carries no qualifier — the overwhelmingly
 * common case, and the one where the value must be taken at face value.
 */
export function parseUnitScale(text: string | null | undefined): UnitScale | null {
  if (!text) return null;
  const t = String(text).toLowerCase();
  const symbol = t.match(SCALE_SYMBOL_RE) ?? t.match(SCALE_SYMBOL_SUFFIX_RE);
  if (symbol) {
    const hit = scaleFromToken(symbol[1]);
    if (hit) return hit;
  }
  const word = t.match(SCALE_WORD_RE);
  if (word) {
    const hit = scaleFromToken(word[1]);
    if (hit) return hit;
  }
  if (SCALE_THOUSAND_TICK_RE.test(t)) return SCALE_THOUSAND;
  return null;
}

/**
 * True when a cell holds ONLY a unit/scale qualifier (`"$M"`,
 * `"USD millions"`, `"$/MWh"`, `"%"`), i.e. it is a units column rather
 * than the value. Such a cell is stepped over when looking for the
 * value to the right of a label — and its text becomes the scale
 * evidence.
 */
function isPureUnitsCell(raw: string): boolean {
  const s = raw.trim();
  if (s.length === 0 || s.length > 24) return false;
  // Digits disqualify, except the accountant's `'000` / `000s` marker.
  if (/\d/.test(s) && !/^[^\d]*(?:'0{3}|0{3}s)[^\d]*$/.test(s)) return false;
  if (parseUnitScale(s)) return true;
  return /^\(?\s*(?:us\$|usd|\$|€|£)?\s*\/?\s*(?:mwh|kwh|kw(?:\s*-?\s*yr)?|mw|yr|year|%)\s*\)?$/i.test(s);
}

interface ScaleEvidence extends UnitScale {
  /** The literal text the scale was read from — shown in the warning. */
  evidence: string;
}

/**
 * Header/units-row fallback: when neither the label nor a units cell
 * carries a qualifier, a model usually declares it once at the top of
 * the sheet ("Inputs (US$ millions)") or above the value's column.
 * Only rows ABOVE the value are considered — a header cannot sit under
 * its own data.
 */
function findScaleInHeaderRows(
  sheet: XLSX.WorkSheet,
  valueRow: number,
  valueCol: number,
): ScaleEvidence | null {
  if (!sheet['!ref']) return null;
  let range: XLSX.Range;
  try {
    range = XLSX.utils.decode_range(sheet['!ref']);
  } catch {
    return null;
  }
  const lastHeaderRow = Math.min(range.s.r + SCALE_HEADER_SCAN_ROWS - 1, range.e.r, valueRow - 1);
  let sameColumn: ScaleEvidence | null = null;
  let anyColumn: ScaleEvidence | null = null;
  for (let row = range.s.r; row <= lastHeaderRow; row += 1) {
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })] as XLSX.CellObject | undefined;
      if (!cell || typeof cell.v !== 'string') continue;
      const scale = parseUnitScale(cell.v);
      if (!scale) continue;
      const found: ScaleEvidence = { ...scale, evidence: String(cell.v).trim() };
      if (col === valueCol) { sameColumn = sameColumn ?? found; }
      else { anyColumn = anyColumn ?? found; }
    }
  }
  // The cell directly above the value is the strongest column header.
  if (valueRow - 1 >= range.s.r) {
    const above = sheet[XLSX.utils.encode_cell({ r: valueRow - 1, c: valueCol })] as XLSX.CellObject | undefined;
    if (above && typeof above.v === 'string') {
      const scale = parseUnitScale(above.v);
      if (scale) return { ...scale, evidence: String(above.v).trim() };
    }
  }
  return sameColumn ?? anyColumn;
}

// ─────────────────────────────────────────────────────────────────────
// Review flags (F3 / A8 / A10) — the extractor FLAGS, it does not guess
// ─────────────────────────────────────────────────────────────────────

/**
 * $/kW plausibility band for a generation project. Below the floor the
 * model is almost certainly quoted in $k/$M and was read at face value;
 * above the ceiling a per-MW or per-unit figure has been read as $/kW.
 * The extractor NEVER silently corrects across these bounds — it flags.
 */
export const CAPEX_PER_KW_IMPLAUSIBLE_LOW_USD = 100;
export const CAPEX_PER_KW_IMPLAUSIBLE_HIGH_USD = 25_000;

export type ExtractionFlagCode =
  | 'implausible-capex-per-kw'
  | 'ambiguous-debt-equity-ratio';

export interface ExtractionFlagCandidate {
  /** Plain-language reading, e.g. "read as debt:equity — 2.33:1". */
  label: string;
  /** The value that reading implies, in the field's canonical units. */
  value: number;
}

export interface ExtractionReviewFlag {
  code: ExtractionFlagCode;
  /** Canonical fields the caution attaches to (review-modal rows). */
  fields: FinancialField[];
  /** Plain-language caution. Never implies the extractor fixed anything. */
  message: string;
  /**
   * Competing readings the USER must choose between. Present only when
   * the extractor refuses to guess (A8's debt:equity vs equity:debt).
   */
  candidates?: ExtractionFlagCandidate[];
  /**
   * true ⇒ the affected rows must be decided one by one. The review
   * modal excludes them from "Accept all" and pulls them out of the
   * auto-accepted high-confidence bucket.
   */
  blocksBulkAccept: boolean;
}

// ─────────────────────────────────────────────────────────────────────
// Currency (A10) — detect, declare, and REQUIRE a rate. Never convert.
// ─────────────────────────────────────────────────────────────────────

export interface DetectedCurrency {
  code: CurrencyCode;
  /** The literal cell text the code was read from. */
  evidence: string;
  /** `Sheet!A1` of that cell. */
  cellReference?: string;
}

/** Symbols with no plausible non-currency reading in a financial model. */
const CURRENCY_SYMBOLS: Array<[RegExp, CurrencyCode]> = [
  [/R\$/i, 'BRL'],
  [/€/, 'EUR'],
  [/£/, 'GBP'],
  [/₹/, 'INR'],
  [/₩/, 'KRW'],
  [/₺/, 'TRY'],
  [/₪/, 'ILS'],
];

/**
 * ISO-4217 codes we recognise. `$` on its own stays USD — it is what
 * every writer already assumes and disambiguating CAD/AUD/USD from a
 * bare `$` is not possible.
 */
const CURRENCY_CODES = [
  'EUR', 'GBP', 'BWP', 'ZAR', 'CAD', 'AUD', 'NZD', 'JPY', 'CNY', 'INR',
  'MXN', 'BRL', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'NGN', 'KES', 'AED',
  'SAR', 'KRW', 'SGD', 'HKD', 'TRY', 'ILS',
];
const CURRENCY_CODE_RE = new RegExp(`\\b(${CURRENCY_CODES.join('|')}|USD)\\b`, 'i');

/** Scan cap so a 200k-cell workbook cannot stall the upload. */
const CURRENCY_SCAN_CELL_CAP = 5_000;

/**
 * Read a currency out of one cell's text — but only from a UNIT
 * POSITION, so `"CAD drawing (rev B)"` is not read as Canadian dollars.
 * A unit position is: inside a parenthetical, the whole cell, or
 * immediately before a scale word or a `/MWh`-style denominator.
 */
export function detectCurrencyInText(text: string): { code: CurrencyCode; evidence: string } | null {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed.length > 80) return null;
  for (const [re, code] of CURRENCY_SYMBOLS) {
    if (re.test(trimmed)) return { code, evidence: trimmed };
  }
  const parentheticals = Array.from(trimmed.matchAll(/\(([^)]*)\)/g)).map(m => m[1]);
  const candidates = [...parentheticals, trimmed];
  for (const candidate of candidates) {
    const m = candidate.match(CURRENCY_CODE_RE);
    if (!m) continue;
    const code = m[1].toUpperCase();
    const isParenthetical = parentheticals.includes(candidate);
    const isWholeCell = /^[\s(]*[a-z]{3}\s*(?:'?0{3}s?|mm|bn|[kmb])?[\s)]*$/i.test(candidate.trim());
    const beforeUnit = new RegExp(
      `\\b${code}\\b\\s*(?:'?0{3}s?|mm\\b|bn\\b|[kmb]\\b|thousands?|millions?|billions?|\\/\\s*(?:mwh|kwh|kw|mw))`,
      'i',
    ).test(candidate);
    if (isParenthetical || isWholeCell || beforeUnit) return { code, evidence: trimmed };
  }
  return null;
}

function detectWorkbookCurrency(workbook: XLSX.WorkBook): DetectedCurrency | null {
  let scanned = 0;
  let usdConfirmation: DetectedCurrency | null = null;
  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet || !sheet['!ref']) continue;
    let range: XLSX.Range;
    try { range = XLSX.utils.decode_range(sheet['!ref']); } catch { continue; }
    for (let row = range.s.r; row <= range.e.r; row += 1) {
      for (let col = range.s.c; col <= range.e.c; col += 1) {
        if (scanned >= CURRENCY_SCAN_CELL_CAP) return usdConfirmation;
        const addr = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = sheet[addr] as XLSX.CellObject | undefined;
        if (!cell || typeof cell.v !== 'string') continue;
        scanned += 1;
        const hit = detectCurrencyInText(cell.v);
        if (!hit) continue;
        const found: DetectedCurrency = { ...hit, cellReference: `${sheetName}!${addr}` };
        // A non-USD hit wins immediately; a USD hit is only a fallback
        // confirmation (a model may say "USD" on one tab and "EUR" on
        // the tab that actually holds the numbers).
        if (found.code !== 'USD') return found;
        usdConfirmation = usdConfirmation ?? found;
      }
    }
  }
  return usdConfirmation;
}

/** True when the extraction produced at least one money-valued field. */
export function hasMonetaryValues(assumptions: Partial<FinancialAssumptionsCore>): boolean {
  for (const field of MONETARY_FIELDS) {
    if (assumptions[field] != null) return true;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────
// Source pinning (A11)
// ─────────────────────────────────────────────────────────────────────

/**
 * SHA-256 of the uploaded bytes, lower-case hex (64 chars). Pins a
 * report to the exact workbook it was generated from.
 *
 * Returns null when WebCrypto is unavailable (insecure context / very
 * old browser) — the caller degrades to `sourceFileName` only rather
 * than blocking the upload, and must say so.
 */
export async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  // Copy into a standalone ArrayBuffer so a view over a larger buffer
  // (or a SharedArrayBuffer) hashes only the bytes it describes.
  const buffer = view.slice().buffer as ArrayBuffer;
  const digest = await subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Public entry point.
 *
 * Accepts a `File` (browser path) or a `Uint8Array | ArrayBuffer`
 * (node / test path). Reads the workbook into memory, runs the three
 * tiers, and returns the merged result.
 *
 * Soft-fails on a malformed workbook: returns an empty result with
 * a `warnings: ['Could not read workbook: <error>']` entry.
 */
export async function extractFinancialModel(
  file: File | Uint8Array | ArrayBuffer,
  options: ExtractFinancialModelOptions = {},
): Promise<ExtractionResult> {
  const now = options.now ?? new Date().toISOString();
  let buffer: ArrayBuffer | Uint8Array;
  try {
    buffer = file instanceof ArrayBuffer || file instanceof Uint8Array
      ? file
      : await file.arrayBuffer();
  } catch (err) {
    return emptyResult([`Could not read file: ${(err as Error)?.message ?? err}`]);
  }

  let workbook: XLSX.WorkBook;
  try {
    workbook = XLSX.read(buffer, { type: 'array', cellNF: false, cellText: false, cellDates: true });
  } catch (err) {
    return emptyResult([`Could not parse workbook: ${(err as Error)?.message ?? err}`]);
  }

  const warnings: string[] = [];
  const reviewFlags: ExtractionReviewFlag[] = [];
  const assumptions: Partial<FinancialAssumptionsCore> = {};
  const provenance: Partial<Record<FinancialField, FieldProvenance>> = {};

  // ─── Tier 1 — named ranges ──────────────────────────────────────
  const tier1 = extractByNamedRanges(workbook);
  for (const [field, hit] of Object.entries(tier1.fields) as Array<[FinancialField, CellHit]>) {
    if (assumptions[field] !== undefined) continue;
    applyHit(assumptions, provenance, field, hit, 'high', 'extracted-named-range', now, warnings, reviewFlags);
  }
  warnings.push(...tier1.warnings);

  // ─── Tier 2 — label matching, restricted to still-missing fields ─
  const missingAfterTier1 = canonicalFields().filter(f => assumptions[f] === undefined);
  const tier2 = extractByLabels(workbook, missingAfterTier1);
  for (const [field, hit] of Object.entries(tier2.fields) as Array<[FinancialField, CellHit]>) {
    if (assumptions[field] !== undefined) continue;
    applyHit(assumptions, provenance, field, hit, 'medium', 'extracted-label-match', now, warnings, reviewFlags);
  }
  warnings.push(...tier2.warnings);

  // ─── Tier 3 — LLM, restricted to still-missing fields ───────────
  const missingAfterTier2 = canonicalFields().filter(f => assumptions[f] === undefined);
  if (options.llmExtractor && missingAfterTier2.length > 0) {
    try {
      const sheetsMarkdown = workbook.SheetNames.map(sheet => ({
        sheet,
        markdown: renderSheetAsMarkdown(workbook.Sheets[sheet]),
      }));
      const tier3 = await options.llmExtractor(missingAfterTier2, sheetsMarkdown);
      for (const [field, hit] of Object.entries(tier3) as Array<[FinancialField, { value: number | string; sheet?: string; cell?: string }]>) {
        if (assumptions[field] !== undefined) continue;
        const baseRef = hit.sheet && hit.cell ? `${hit.sheet}!${hit.cell}` : hit.cell;
        // The debt-fraction ambiguity (A8) is a property of the VALUE, not
        // of the tier that found it — an LLM guess needs the same guard.
        if (field === 'debtToEquityRatio') {
          const reading = interpretDebtFraction(hit.value);
          if (reading == null) continue;
          (assumptions as Record<string, unknown>)[field] = reading.value;
          provenance[field] = {
            source: 'extracted-llm',
            confidence: 'low',
            cellReference: reading.note && baseRef ? annotateCellReference(baseRef, reading.note) : baseRef,
            extractedAt: now,
          };
          if (reading.warning) warnings.push(reading.warning);
          if (reading.flag) reviewFlags.push(reading.flag);
          continue;
        }
        const numeric = toFieldNumeric(field, hit.value);
        if (numeric == null && !isStringField(field)) continue;
        if (isStringField(field)) {
          (assumptions as Record<string, unknown>)[field] = String(hit.value);
        } else {
          (assumptions as Record<string, unknown>)[field] = numeric;
        }
        provenance[field] = {
          source: 'extracted-llm',
          confidence: 'low',
          cellReference: baseRef,
          extractedAt: now,
        };
      }
    } catch (err) {
      warnings.push(`LLM extractor failed: ${(err as Error)?.message ?? err}`);
    }
  }

  // ─── Post-extraction plausibility (F3) ──────────────────────────
  // Runs LAST so it sees the merged picture across all three tiers.
  const capexFlag = buildCapexPerKwFlag(assumptions);
  if (capexFlag) reviewFlags.push(capexFlag);

  // ─── Currency (A10) — declare, never convert ────────────────────
  const detectedCurrency = detectWorkbookCurrency(workbook);

  // Summary counts
  let high = 0;
  let medium = 0;
  let low = 0;
  for (const fp of Object.values(provenance)) {
    if (!fp) continue;
    if (fp.confidence === 'high') high += 1;
    else if (fp.confidence === 'medium') medium += 1;
    else if (fp.confidence === 'low') low += 1;
  }
  const missing = canonicalFields().filter(f => assumptions[f] === undefined).length;

  return {
    assumptions,
    fieldProvenance: provenance,
    warnings,
    reviewFlags,
    detectedCurrency,
    summary: { highConfidence: high, mediumConfidence: medium, lowConfidence: low, missing },
  };
}

/**
 * $/kW plausibility check (F3). Uses the extracted `capexPerKwUsd` when
 * present, else derives it from total CAPEX and capacity — which is how
 * a unit-scale miss ("$M" read as "$") surfaces: $500 over 300 MW is
 * $0.0017/kW.
 *
 * FLAGS ONLY. Correcting it would mean guessing which of the two inputs
 * is wrong, and a wrong guess is the same silent-wrong-number failure.
 */
function buildCapexPerKwFlag(
  assumptions: Partial<FinancialAssumptionsCore>,
): ExtractionReviewFlag | null {
  const capexTotal = assumptions.capexTotalUsd;
  const capacityMW = assumptions.capacityMW;
  const derived = capexTotal != null && capacityMW != null && capacityMW > 0
    ? capexTotal / (capacityMW * 1000)
    : null;
  const perKw = assumptions.capexPerKwUsd ?? derived;
  if (perKw == null || !Number.isFinite(perKw)) return null;
  if (perKw >= CAPEX_PER_KW_IMPLAUSIBLE_LOW_USD && perKw <= CAPEX_PER_KW_IMPLAUSIBLE_HIGH_USD) return null;

  const fields: FinancialField[] = (['capexTotalUsd', 'capacityMW', 'capexPerKwUsd'] as FinancialField[])
    .filter(f => assumptions[f] != null);
  if (fields.length === 0) return null;

  const shown = perKw < 1 ? perKw.toPrecision(2) : Math.round(perKw).toLocaleString('en-US');
  const direction = perKw < CAPEX_PER_KW_IMPLAUSIBLE_LOW_USD ? 'far too low' : 'far too high';
  const hint = perKw < CAPEX_PER_KW_IMPLAUSIBLE_LOW_USD
    ? 'A model quoted in $ thousands or $ millions reads this low when the header scale is missing.'
    : 'A figure quoted per MW (or for the whole plant) reads this high when taken as $/kW.';
  return {
    code: 'implausible-capex-per-kw',
    fields,
    message:
      `These values imply $${shown}/kW, which is ${direction} for a generation project `
      + `(plausible range $${CAPEX_PER_KW_IMPLAUSIBLE_LOW_USD.toLocaleString('en-US')}–`
      + `$${CAPEX_PER_KW_IMPLAUSIBLE_HIGH_USD.toLocaleString('en-US')}/kW). ${hint} `
      + 'Nothing was changed — check the units and confirm or correct each value.',
    blocksBulkAccept: true,
  };
}

// ─────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────

/**
 * One located value plus everything needed to read its UNITS: the label
 * that introduced it, a units cell sitting between label and value, and
 * the scale resolved from label → units cell → sheet header row.
 */
interface CellHit {
  rawValue: unknown;
  cellReference: string;
  /** Label text (tier-2 match, or the cell left of a named range). */
  label?: string;
  /** Scale qualifier that applies to this value, when one was found. */
  scale?: ScaleEvidence;
}

function emptyResult(warnings: string[]): ExtractionResult {
  return {
    assumptions: {},
    fieldProvenance: {},
    warnings,
    reviewFlags: [],
    detectedCurrency: null,
    summary: { highConfidence: 0, mediumConfidence: 0, lowConfidence: 0, missing: canonicalFields().length },
  };
}

function canonicalFields(): FinancialField[] {
  // Subset that the extractor knows how to fill. We deliberately
  // exclude fields like `technology` / `depreciationMethod` (typed
  // unions, not numbers) — those land via the manual form / baseline.
  return [
    'capacityMW', 'capacityFactorPct', 'projectLifeYears', 'inServiceDate',
    'capexTotalUsd', 'capexPerKwUsd', 'constructionMonths',
    'decommissioningUsd', 'workingCapitalUsd', 'contingencyPct',
    'fixedOmPerKwYearUsd', 'variableOmPerMwhUsd', 'fuelCostPerMwhUsd',
    'insuranceAndPropertyTaxPct',
    'debtToEquityRatio', 'debtRatePct', 'equityHurdlePct', 'waccPct',
    'taxRatePct', 'itcPct', 'ptcPerMwhUsd',
    'ppaPricePerMwhUsd', 'ppaEscalatorPctPerYear', 'ppaTermYears',
    'lcoeNominalPerMwhUsd', 'lcoeRealPerMwhUsd', 'npvUsd',
    'projectIrrPct', 'equityIrrPct', 'paybackYears',
  ];
}

function isStringField(field: FinancialField): boolean {
  return field === 'inServiceDate';
}

/** Append a re-reading note to a provenance cellReference. */
function annotateCellReference(cellReference: string, note: string): string {
  return `${cellReference}${PROVENANCE_NOTE_SEPARATOR}${note}`;
}

function applyHit(
  assumptions: Partial<FinancialAssumptionsCore>,
  provenance: Partial<Record<FinancialField, FieldProvenance>>,
  field: FinancialField,
  hit: CellHit,
  confidence: FieldProvenanceConfidence,
  source: 'extracted-named-range' | 'extracted-label-match',
  now: string,
  warnings: string[],
  reviewFlags: ExtractionReviewFlag[],
): void {
  if (isStringField(field)) {
    const text = toFieldString(hit.rawValue);
    if (text == null) return;
    (assumptions as Record<string, unknown>)[field] = text;
    provenance[field] = { source, confidence, cellReference: hit.cellReference, extractedAt: now };
    return;
  }

  // `debtToEquityRatio` (A8) is neither a plain number nor a percent —
  // the same cell can be a fraction, a percentage or a colon ratio, and
  // the three readings differ by 100x. Resolve it separately.
  if (field === 'debtToEquityRatio') {
    const reading = interpretDebtFraction(hit.rawValue, hit.label);
    if (reading == null) return;
    (assumptions as Record<string, unknown>)[field] = reading.value;
    provenance[field] = {
      source,
      // An ambiguous reading is never high-confidence, whatever tier found it.
      confidence: reading.flag ? 'medium' : confidence,
      cellReference: reading.note
        ? annotateCellReference(hit.cellReference, reading.note)
        : hit.cellReference,
      extractedAt: now,
    };
    if (reading.warning) warnings.push(reading.warning);
    if (reading.flag) reviewFlags.push(reading.flag);
    return;
  }

  // Unit scale (F3) — only ever applied to USD MAGNITUDE fields, and
  // never on top of a suffix the cell already carried ("$500M").
  const scaleApplies = hit.scale != null && USD_MAGNITUDE_FIELDS.has(field);
  const value = toFieldNumeric(field, hit.rawValue, {
    scaleMultiplier: scaleApplies ? hit.scale!.multiplier : undefined,
  });
  if (value == null) return;
  (assumptions as Record<string, unknown>)[field] = value;

  const rescaled = scaleApplies && value !== toFieldNumeric(field, hit.rawValue);
  provenance[field] = {
    source,
    confidence,
    cellReference: rescaled
      ? annotateCellReference(hit.cellReference, `read as ${hit.scale!.label}`)
      : hit.cellReference,
    extractedAt: now,
  };
  if (rescaled) {
    warnings.push(
      `"${hit.scale!.evidence}" reads ${hit.cellReference} as ${hit.scale!.label} — `
      + `${formatRaw(hit.rawValue)} taken as ${value.toLocaleString('en-US')}.`,
    );
  }
}

function formatRaw(raw: unknown): string {
  if (typeof raw === 'number') return raw.toLocaleString('en-US');
  return String(raw);
}

interface DebtFractionReading {
  /** Value written to the field, in its canonical units. */
  value: number;
  /** Provenance note when the extractor re-read the cell. */
  note?: string;
  /** Warning for the top-of-modal strip when a conversion was applied. */
  warning?: string;
  /** Set when the reading is AMBIGUOUS — value left untouched. */
  flag?: ExtractionReviewFlag;
}

/**
 * Labels that name a SHARE of total capital — the reading for which a
 * value of 70 unambiguously means 0.70. `Debt:Equity` / `D:E Ratio` /
 * `Leverage Ratio` are deliberately NOT here: they name a ratio.
 */
const DEBT_SHARE_LABEL_RE =
  /\b(debt\s*(share|fraction|percent|percentage|weight(ing)?|portion)|debt\s*%|%\s*debt|debt\s*(to|\/)\s*(cap|capital|total)|gearing\s*%)/i;

/** `70:30`, `2.33 : 1` — the colon form is always a ratio. */
const COLON_RATIO_RE = /^\s*(-?\d+(?:\.\d+)?)\s*:\s*(-?\d+(?:\.\d+)?)\s*$/;

function debtCandidatesFromRatio(d: number): ExtractionFlagCandidate[] {
  return [
    { label: `read as debt:equity (${round4(d)}:1) ⇒ debt fraction`, value: round4(d / (1 + d)) },
    { label: `read as equity:debt (1:${round4(d)}) ⇒ debt fraction`, value: round4(1 / (1 + d)) },
  ];
}

function round4(n: number): number {
  return Math.round(n * 10_000) / 10_000;
}

/**
 * Resolve a `debtToEquityRatio` cell against the field's documented
 * contract — a debt FRACTION in [0, 1].
 *
 * | cell                              | reading                                   |
 * |-----------------------------------|-------------------------------------------|
 * | 0.7                               | 0.7 — already a fraction                  |
 * | "70%" / "Debt Share" 70           | 0.7 — percent-looking ⇒ /100              |
 * | "D:E Ratio" 2.33 / "70:30"        | AMBIGUOUS — flagged, value left as-is     |
 * | 233                               | AMBIGUOUS — outside every convention      |
 *
 * The ambiguous cases carry BOTH candidate readings so the user picks;
 * `d/(1+d)` is never applied on the extractor's own authority because
 * debt:equity and equity:debt are indistinguishable from the cell.
 */
export function interpretDebtFraction(raw: unknown, label?: string): DebtFractionReading | null {
  const rawText = typeof raw === 'string' ? raw.trim() : '';

  // Colon form — unambiguously a ratio, ambiguous in its direction.
  const colon = rawText.match(COLON_RATIO_RE);
  if (colon) {
    const a = Number(colon[1]);
    const b = Number(colon[2]);
    if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return null;
    const d = a / b;
    return {
      value: round4(d),
      note: 'ratio — needs review',
      flag: {
        code: 'ambiguous-debt-equity-ratio',
        fields: ['debtToEquityRatio'],
        message:
          `"${rawText}" is a ratio, but this field stores the DEBT FRACTION of total capital `
          + '(0–1). The sheet does not say whether it means debt:equity or equity:debt, so nothing '
          + 'was converted. Pick the reading you meant.',
        candidates: debtCandidatesFromRatio(d),
        blocksBulkAccept: true,
      },
    };
  }

  const n = toFieldNumeric('debtToEquityRatio', raw);
  if (n == null) return null;

  // Already a fraction — the documented shape.
  if (n >= 0 && n <= 1) return { value: n };

  const percentLooking = rawText.endsWith('%') || (label != null && DEBT_SHARE_LABEL_RE.test(label));
  if (n > 1 && n <= 100 && percentLooking) {
    const value = round4(n / 100);
    return {
      value,
      note: 'read as %',
      warning: `Debt share ${n} read as ${n}% ⇒ debt fraction ${value}.`,
    };
  }

  const candidates = n > 1
    ? [
      ...(n <= 100 ? [{ label: `read as a percentage (${n}%) ⇒ debt fraction`, value: round4(n / 100) }] : []),
      ...debtCandidatesFromRatio(n),
    ]
    : [];
  return {
    value: n,
    note: 'needs review',
    flag: {
      code: 'ambiguous-debt-equity-ratio',
      fields: ['debtToEquityRatio'],
      message:
        `${n} cannot be a debt FRACTION (this field stores 0–1, e.g. 0.7 for 70:30). `
        + 'It could be a percentage or a debt:equity ratio, and the two differ — nothing was '
        + 'converted. Pick the reading you meant, or enter the fraction directly. '
        + 'Left as-is, WACC cannot be derived and LCOE/NPV stay blank.',
      ...(candidates.length > 0 ? { candidates } : {}),
      blocksBulkAccept: true,
    },
  };
}

/**
 * Normalise a name string for fuzzy comparison: uppercase, strip
 * non-alphanumerics. `LCOE_NOMINAL` ⇒ `LCOENOMINAL`, `lcoe.nominal`
 * ⇒ `LCOENOMINAL`.
 */
function normaliseName(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function extractByNamedRanges(workbook: XLSX.WorkBook): { fields: Partial<Record<FinancialField, CellHit>>; warnings: string[] } {
  const fields: Partial<Record<FinancialField, CellHit>> = {};
  const warnings: string[] = [];
  const names = workbook.Workbook?.Names ?? [];
  if (names.length === 0) return { fields, warnings };

  // Pre-normalised lookup so case / underscore variants collide.
  const lookup = new Map<string, FinancialField>();
  for (const [rangeName, field] of Object.entries(NAMED_RANGE_MAP)) {
    lookup.set(normaliseName(rangeName), field);
  }

  for (const def of names) {
    const key = normaliseName(def.Name ?? '');
    const field = lookup.get(key);
    if (!field) continue;
    if (fields[field]) continue;  // first hit wins for tier 1
    const value = resolveCellRef(workbook, def.Ref);
    if (value == null) {
      warnings.push(`Named range ${def.Name} points to ${def.Ref} but the cell is empty`);
      continue;
    }
    // A named range carries no label of its own, so the units can only
    // come from the row's label cell, a units cell, or the sheet header.
    const label = value.label ?? def.Name;
    fields[field] = {
      rawValue: value.rawValue,
      cellReference: value.cellReference,
      label,
      ...(resolveScale(value.sheet, label, value.unitsText, value.row, value.col) ?? {}),
    };
  }
  return { fields, warnings };
}

/**
 * Scale resolution order: the label itself → a units cell between label
 * and value → the sheet's header/units band. First hit wins.
 */
function resolveScale(
  sheet: XLSX.WorkSheet,
  label: string | undefined,
  unitsText: string | undefined,
  row: number,
  col: number,
): { scale: ScaleEvidence } | null {
  const fromLabel = parseUnitScale(label);
  if (fromLabel) return { scale: { ...fromLabel, evidence: label!.trim() } };
  const fromUnits = parseUnitScale(unitsText);
  if (fromUnits) return { scale: { ...fromUnits, evidence: unitsText!.trim() } };
  const fromHeader = findScaleInHeaderRows(sheet, row, col);
  return fromHeader ? { scale: fromHeader } : null;
}

/** Nearest string cell to the LEFT of a value, within `span` columns. */
function labelToTheLeft(sheet: XLSX.WorkSheet, row: number, col: number, span = 3): string | undefined {
  for (let step = 1; step <= span; step += 1) {
    if (col - step < 0) break;
    const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col - step })] as XLSX.CellObject | undefined;
    if (!cell || cell.v == null || cell.v === '') continue;
    if (typeof cell.v !== 'string') break;
    const text = cell.v.trim();
    if (text.length === 0) continue;
    // A units cell sits between the label and the value — keep walking.
    if (isPureUnitsCell(text)) continue;
    return text;
  }
  return undefined;
}

/**
 * Resolve a defined-name reference like `'Inputs'!$B$14` or
 * `Sheet1!B14:B14` to a single cell value + display reference.
 * Ranges spanning >1 cell take the top-left cell and add a warning
 * via the caller.
 */
function resolveCellRef(workbook: XLSX.WorkBook, rawRef: string | undefined): {
  rawValue: unknown;
  cellReference: string;
  sheet: XLSX.WorkSheet;
  row: number;
  col: number;
  label?: string;
  unitsText?: string;
} | null {
  if (!rawRef) return null;
  // Strip leading '=' (some named-name formulas are stored as '=Sheet!Cell').
  let ref = rawRef.replace(/^=/, '').trim();
  // The defined-name spec is `Sheet!A1:B2` or `'Sheet name'!A1`. Pull the
  // sheet + first cell address.
  const m = ref.match(/^'?([^'!]+)'?!(\$?[A-Z]+\$?\d+)/i);
  if (!m) return null;
  const sheetName = m[1];
  const cellAddr = m[2].replace(/\$/g, '');
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return null;
  const cell = sheet[cellAddr] as XLSX.CellObject | undefined;
  if (!cell || cell.v === undefined || cell.v === null || cell.v === '') return null;
  let decoded: { r: number; c: number };
  try {
    decoded = XLSX.utils.decode_cell(cellAddr);
  } catch {
    return null;
  }
  const leftCell = decoded.c > 0
    ? (sheet[XLSX.utils.encode_cell({ r: decoded.r, c: decoded.c - 1 })] as XLSX.CellObject | undefined)
    : undefined;
  const unitsText = leftCell && typeof leftCell.v === 'string' && isPureUnitsCell(leftCell.v)
    ? leftCell.v.trim()
    : undefined;
  return {
    rawValue: cell.v,
    cellReference: `${sheetName}!${cellAddr}`,
    sheet,
    row: decoded.r,
    col: decoded.c,
    label: labelToTheLeft(sheet, decoded.r, decoded.c),
    unitsText,
  };
}

function extractByLabels(workbook: XLSX.WorkBook, fields: FinancialField[]): { fields: Partial<Record<FinancialField, CellHit>>; warnings: string[] } {
  const result: Partial<Record<FinancialField, CellHit>> = {};
  const warnings: string[] = [];
  if (fields.length === 0) return { fields: result, warnings };

  // Per-field candidates so we can pick a preferred sheet at the end.
  const candidates: Partial<Record<FinancialField, CellHit[]>> = {};
  for (const f of fields) candidates[f] = [];

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet || !sheet['!ref']) continue;
    const range = XLSX.utils.decode_range(sheet['!ref']);
    for (let row = range.s.r; row <= range.e.r; row += 1) {
      for (let col = range.s.c; col <= range.e.c; col += 1) {
        const addr = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = sheet[addr] as XLSX.CellObject | undefined;
        if (!cell || typeof cell.v !== 'string') continue;
        const cellText = String(cell.v).trim();
        if (cellText.length === 0) continue;
        const normalised = cellText.toLowerCase();
        for (const field of fields) {
          const synonyms = LABEL_SYNONYMS[field];
          if (!synonyms) continue;
          if (!labelMatches(normalised, synonyms)) continue;
          // Found a candidate label. Value lives in the right neighbour
          // (preferred) or the cell below.
          const neighbourValue = readNeighbour(sheet, row, col);
          if (neighbourValue == null) continue;
          candidates[field]!.push({
            rawValue: neighbourValue.rawValue,
            cellReference: `${sheetName}!${neighbourValue.cellReference}`,
            label: cellText,
            ...(resolveScale(
              sheet,
              cellText,
              neighbourValue.unitsText,
              neighbourValue.row,
              neighbourValue.col,
            ) ?? {}),
          });
        }
      }
    }
  }

  for (const field of fields) {
    const hits = candidates[field] ?? [];
    if (hits.length === 0) continue;
    // Prefer hits on `inputs` / `summary` / `outputs`-style sheets.
    const sorted = hits.slice().sort((a, b) => sheetPriority(b.cellReference) - sheetPriority(a.cellReference));
    if (hits.length > 1) {
      warnings.push(`Found ${hits.length} cells labelled "${hits[0].label}" — picked ${sorted[0].cellReference}`);
    }
    result[field] = sorted[0];
  }
  return { fields: result, warnings };
}

function labelMatches(normalisedCellText: string, synonyms: string[]): boolean {
  for (const synonym of synonyms) {
    const normSyn = synonym.toLowerCase();
    if (normalisedCellText === normSyn) return true;
    if (normalisedCellText.startsWith(`${normSyn}:`)) return true;
    if (normalisedCellText.startsWith(`${normSyn} `)) return true;
    if (normalisedCellText === `${normSyn}:`) return true;
    // Allow trailing parentheticals: "LCOE (nominal, $/MWh)" matches "LCOE".
    if (normalisedCellText.startsWith(`${normSyn} (`)) return true;
  }
  return false;
}

function readNeighbour(
  sheet: XLSX.WorkSheet,
  row: number,
  col: number,
): { rawValue: unknown; cellReference: string; row: number; col: number; unitsText?: string } | null {
  // Right neighbour first, stepping over a UNITS column ("$M", "$/MWh")
  // when the model puts one between the label and the value. Before
  // this, `Total CAPEX | $M | 500` yielded the string "$M" and the
  // field was dropped on the floor.
  let unitsText: string | undefined;
  for (let step = 1; step <= 1 + MAX_UNITS_CELLS_BETWEEN; step += 1) {
    const addr = XLSX.utils.encode_cell({ r: row, c: col + step });
    const cell = sheet[addr] as XLSX.CellObject | undefined;
    if (!cell || cell.v === undefined || cell.v === null || cell.v === '') break;
    if (typeof cell.v === 'string' && isPureUnitsCell(cell.v)) {
      unitsText = unitsText ?? cell.v.trim();
      continue;
    }
    return { rawValue: cell.v, cellReference: addr, row, col: col + step, unitsText };
  }
  // Below
  const below = XLSX.utils.encode_cell({ r: row + 1, c: col });
  const belowCell = sheet[below] as XLSX.CellObject | undefined;
  if (belowCell && belowCell.v !== undefined && belowCell.v !== null && belowCell.v !== '') {
    return { rawValue: belowCell.v, cellReference: below, row: row + 1, col, unitsText };
  }
  return null;
}

function sheetPriority(cellRef: string): number {
  const sheet = cellRef.split('!')[0]?.toLowerCase() ?? '';
  for (let i = 0; i < PREFERRED_SHEETS.length; i += 1) {
    if (sheet.includes(PREFERRED_SHEETS[i])) return PREFERRED_SHEETS.length - i;
  }
  return 0;
}

/**
 * Convert a raw cell value to the canonical numeric value for a
 * field. Handles:
 *   - 0.0-1.0 fractions on percent fields ⇒ multiply by 100
 *   - Strings like "92%" / "$1,625,000,000" ⇒ strip and parse
 *   - Excel dates accidentally landing on numeric fields ⇒ reject
 *   - Negative monetary values that shouldn't be ⇒ keep (the form's
 *     validator flags them)
 *
 * Returns null when the value can't be interpreted.
 */
export interface ToFieldNumericOptions {
  /**
   * Scale multiplier read from the label / units cell / header row
   * (`$k` ⇒ 1e3, `$M` ⇒ 1e6, `$bn` ⇒ 1e9). Applied ONLY to
   * `USD_MAGNITUDE_FIELDS`, and never on top of a suffix the cell
   * already carried ("$500M" is 5e8 whatever the header says).
   */
  scaleMultiplier?: number;
}

export function toFieldNumeric(
  field: FinancialField,
  raw: unknown,
  options: ToFieldNumericOptions = {},
): number | null {
  if (raw == null) return null;
  let n: number;
  /** Set when the cell text carried its own magnitude suffix. */
  let inlineMultiplier: number | null = null;
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    n = raw;
  } else if (raw instanceof Date) {
    return null;
  } else if (typeof raw === 'string') {
    let cleaned = raw
      .replace(/[$,\s]/g, '')
      .replace(/%$/, '')
      .replace(/^\(/, '-')
      .replace(/\)$/, '')
      .trim();
    // Inline magnitude suffix — "500M", "1.6bn", "250k". Only honoured
    // on USD magnitudes; on a rate field a trailing letter is a typo,
    // not a scale, and must keep failing to parse.
    if (USD_MAGNITUDE_FIELDS.has(field)) {
      const suffix = cleaned.match(/^(-?\d+(?:\.\d+)?)(mm|bn|[kmb])$/i);
      if (suffix) {
        const scale = scaleFromToken(suffix[2].toLowerCase());
        if (scale) {
          inlineMultiplier = scale.multiplier;
          cleaned = suffix[1];
        }
      }
    }
    if (cleaned.length === 0) return null;
    const parsed = Number(cleaned);
    if (!Number.isFinite(parsed)) return null;
    n = parsed;
    // Strings ending in % already had the symbol stripped — they
    // arrive as the displayed number (e.g. "92%" → 92), so no
    // additional fraction-to-percent conversion needed.
    if (PERCENT_FIELDS.has(field) && !raw.endsWith('%') && Math.abs(n) <= 1) {
      n = n * 100;
    }
  } else {
    return null;
  }

  // Numeric branch (typeof number) — fractional percentages get
  // expanded to the form's percent convention.
  if (typeof raw === 'number' && PERCENT_FIELDS.has(field) && Math.abs(n) <= 1) {
    n = n * 100;
  }

  // Project IRR is stored as a percent; same fraction rule.
  if (PROJECT_IRR_FIELDS.has(field) && Math.abs(n) <= 1) {
    n = n * 100;
  }

  // $/MW (named range alias for capexPerKw) → divide by 1000.
  // Heuristic: capex per kW > $50,000/kW is implausible; assume the
  // sheet held $/MW and convert. This catches the common mislabel.
  if (field === 'capexPerKwUsd' && n > 50_000) {
    n = n / 1000;
  }

  // Unit scale, last: a `($M)` header multiplies the MAGNITUDE fields
  // only. An inline suffix on the cell wins — otherwise "$500M" under
  // a "($M)" header would be scaled twice.
  const multiplier = inlineMultiplier
    ?? (USD_MAGNITUDE_FIELDS.has(field) ? options.scaleMultiplier ?? null : null);
  if (multiplier != null && multiplier !== 1) {
    n = n * multiplier;
  }

  return n;
}

function toFieldString(raw: unknown): string | null {
  if (raw == null) return null;
  if (raw instanceof Date) {
    return raw.toISOString().slice(0, 10);
  }
  const s = String(raw).trim();
  return s.length === 0 ? null : s;
}

/**
 * Render a worksheet as a compact markdown-ish table — used as the
 * tier-3 LLM context.
 *
 * Each row is `| A | B | C |`. Truncated to ~5000 characters so the
 * LLM token-budget stays modest. Empty rows are dropped.
 */
export function renderSheetAsMarkdown(sheet: XLSX.WorkSheet | undefined): string {
  if (!sheet || !sheet['!ref']) return '';
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false });
  const lines: string[] = [];
  let charsUsed = 0;
  const MAX_CHARS = 5000;
  for (const row of aoa) {
    if (!Array.isArray(row) || row.every(v => v == null || String(v).trim() === '')) continue;
    const text = `| ${row.map(v => (v == null ? '' : String(v).trim())).join(' | ')} |`;
    if (charsUsed + text.length > MAX_CHARS) {
      lines.push('| … truncated … |');
      break;
    }
    lines.push(text);
    charsUsed += text.length;
  }
  return lines.join('\n');
}
